import _sortBy from "lodash/sortBy";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { downloadBlob } from "utils";

import { RET_RAW, apiFetch, apiFetchFormData, appendIEHeaders, appendTokenHeaders } from "../api";
import { Api } from "./__generated__/NewsApi";
import { getAllPwcNotes } from "./articleApi";
import { adaptNewsFromBackend, adaptNewsToBackEnd, toApiFilter } from "./newsDataManipulator";

const PREFIX = process.env.REACT_APP_URL_NEWS_ROOT + "News/";

let _clientSingleton = null;
const client = () => {
  if (_clientSingleton) return _clientSingleton;

  // base url, removing duplicated /api/v1/ and trailing slash
  const baseUrl = process.env.REACT_APP_URL_NEWS_ROOT.replace("/api/v1/", "/").slice(
    0,
    process.env.REACT_APP_URL_NEWS_ROOT.slice(-1) === "/" ? -1 : undefined
  );

  return (_clientSingleton = new Api({
    baseUrl,
    baseApiParams: {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...appendTokenHeaders(),
        ...appendIEHeaders(),
      },
    },
  }));
};

export const getList = (filters, pagination = { pageNumber: 1, pageSize: 6 }) => {
  return apiFetch(
    `${PREFIX}paged?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`,
    { filters: toApiFilter(filters.filters), orderBy: filters.orderBy },
    "POST"
  ).then(adaptNewsFromBackend);
};

export const getDashboard = (filters) => {
  return apiFetch(`${process.env.REACT_APP_URL_NEWS_ROOT}Dashboard`, filters, "POST");
};

export const getListByArticle = (articleId) => {
  return apiFetch(`${PREFIX}byArticle/${articleId}`);
};

export const getNews = (newsId) => {
  return apiFetch(`${PREFIX}${newsId}`).then(adaptNewsFromBackend);
};

export const getNewsTitlesFromIds = (articleIds) => {
  return apiFetch(`${PREFIX}titles`, articleIds, "POST");
};

export const addNews = (news) => {
  return apiFetch(PREFIX, adaptNewsToBackEnd(news), "POST");
};

export const updateNews = (news) => {
  return apiFetch(`${PREFIX}${news.id}`, adaptNewsToBackEnd(news), "PUT");
};

export const deleteNews = (newsId) => {
  return apiFetch(`${PREFIX}${newsId}`, undefined, "DELETE");
};

export const publishNews = (id, publish) => {
  return apiFetch(`${PREFIX}${id}/published`, publish.toString(), "PATCH");
};

export const setArticleId = (newsIds, articleId) => {
  return apiFetch(
    `${PREFIX}ArticleId`,
    {
      ids: newsIds,
      articleId,
    },
    "PATCH"
  );
};

export const isAlive = (newsId) => {
  return apiFetch(`${PREFIX}${newsId}/IsAlive`, undefined, "PATCH");
};

export const downloadTemplate = () => {
  return apiFetch(`${PREFIX}import`, undefined, "GET", RET_RAW)
    .then((res) => res.blob())
    .then((blob) => downloadBlob(blob, "import_news_template.xlsx"));
};

export const importNewsFromExcel = (file) => {
  return apiFetchFormData(`${PREFIX}import`, { importFile: file }, "POST");
};

export const getArticle = (articleId) => {
  return client()
    .api.v1Article3WDetail(articleId)
    .then((res) => res?.data);
};
export function useArticle(articleId) {
  return useQuery(["article", articleId], {
    queryFn: () => getArticle(articleId),
    enabled: !!articleId,
  });
}

export const getArticleNotes = (articleId) => {
  return getAllPwcNotes(articleId);
};
export function useArticleNotes(articleId) {
  return useQuery(["articleNotes", articleId], {
    queryFn: () => getArticleNotes(articleId),
    enabled: !!articleId,
  });
}

export const DEFAULT_MULTIRATING_NOTE_GROUP = "Z";
export const DEFAULT_MULTIRATING_NOTE_ORDER = "99";
export function useArticleMultiRating(articleId) {
  const { data: unsortedNotes, ...hook } = useArticleNotes(articleId);

  const data = useMemo(() => {
    if (!Array.isArray(unsortedNotes)) return;

    const preparedNotes = unsortedNotes.map((n) => {
      const companyName = n.isMultiRating ? `${n.companyName?.trim() || ""} - ${n.ratingTitle?.trim() || ""}`.trim() : n.companyName?.trim();
      return {
        ...n,
        companyName,
        customOrder: String(
          (n.groupRating || DEFAULT_MULTIRATING_NOTE_GROUP) +
            (n.orderRating !== null && n.orderRating !== undefined
              ? String(n.orderRating).padStart(2, "0")
              : DEFAULT_MULTIRATING_NOTE_ORDER) +
            (n.companyName?.trim() || "-")
        )
          .trim()
          .toLowerCase()
          .replaceAll(" ", ""),
      };
    });
    return _sortBy(preparedNotes, ["customOrder"]);
  }, [unsortedNotes]);

  return {
    ...hook,
    data,
    unsortedNotes,
  };
}

export const getTopics = (params) => {
  return client()
    .api.v1TopicList(params)
    .then((res) => res?.data);
};
export function useTopics() {
  return useQuery(["engagementTopics"], {
    queryFn: () => getTopics(),
  });
}

export function getSubscriptionAccesses(subscriptionId, params) {
  return client()
    .api.v1SubscriptionAccessDetail(subscriptionId, params)
    .then((res) => res?.data);
}
export function useSubscriptionAccesses(subscriptionId) {
  return useQuery(["subscriptionAccesses", subscriptionId], {
    queryFn: () => getSubscriptionAccesses(subscriptionId),
    enabled: !!subscriptionId,
  });
}

export function useArticleWithSubscriptionTopicAccesses(articleId, subscriptionId) {
  const { data: article, ...articleHook } = useArticle(articleId);
  const articleTopicIds = article?.topicIds;
  const { data: subscriptionAccesses, isLoading: isLoadingAccesses } = useSubscriptionAccesses(subscriptionId);
  const { data: topicsList, isLoading: isLoadingTopics } = useTopics();

  const isLoading = articleHook.isLoading || isLoadingAccesses || isLoadingTopics;

  const data = useMemo(() => {
    if (!articleTopicIds || !topicsList || !subscriptionAccesses) return;
    return articleTopicIds
      .map((id) => ({
        id,
        name: topicsList.find((t) => t.id === id)?.name,
        access: subscriptionAccesses.deniedTopicIds?.includes(id)
          ? "denied"
          : subscriptionAccesses.topicIds?.includes(id)
          ? "selected"
          : "available",
      }))
      .filter((t) => !!t.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subscriptionAccesses, articleTopicIds, topicsList]);

  return { ...articleHook, isLoading, data, article, articleTopicIds, topicsList, subscriptionAccesses };
}

export function getArticleSubscriptionImpact(articleType, businessId, subscriptionId) {
  const businessIdWithoutYear = businessId.split("-").slice(-1)[0];
  return client()
    .api.v1Article3WImpactDetail(articleType, businessIdWithoutYear, subscriptionId)
    .then((res) => res?.data);
}
export function useArticleSubscriptionImpact(articleType, businessId, subscriptionId) {
  return useQuery(["articleImpact", articleType, businessId, subscriptionId], {
    queryFn: () => getArticleSubscriptionImpact(articleType, businessId, subscriptionId),
    enabled: articleType !== undefined && !!businessId && !!subscriptionId,
  });
}
